<template>
  <div v-if="!isLoggedIn">
    <vue-loading v-if="data.loader.registration_config" />
    <div v-else-if="data.registrationReady" class="row">
      <div
        v-if="data.success !== ''"
        class="alert alert-success mb-2"
        role="alert"
        v-dompurify-html="data.success"
      ></div>
      <Form v-else @submit="onSubmitRegistrationForm" class="col-md-12">
        <InfoRequiredFields />
        <div id="wdw-register-form" class="wdw-form">
          <form-control-input
            v-if="data.email_field != null"
            :label="domainStore.translate('user.form.label.email')"
            :type="data.email_field.type"
            :name="data.email_field.code"
            :code="data.email_field.code"
            :required="toBool(data.email_field.required)"
            :public="true"
            :hidden="false"
            :serverError="data.errors[data.email_field.code]"
            v-on:validate="onValidatedFormField"
            :error="domainStore.translate('user.form.message.email-error')"
            :help="domainStore.translate('user.form.message.email-register-help')"
            parentFormId="wdw-register-form"
            defaultvalue=""
          />
          <form-control-input
            v-if="data.email_field != null"
            :label="domainStore.translate('user.form.label.email-repeat')"
            type="email"
            name="email_repeat"
            code="email_repeat"
            :required="true"
            :public="true"
            :hidden="false"
            :serverError="data.errors['email_repeat']"
            v-on:validate="onValidatedFormField"
            :error="domainStore.translate('user.form.message.email-repeat-error')"
            parentFormId="wdw-register-form"
            defaultvalue=""
          />

          <form-control-input
            v-if="data.displayname_field != null"
            :label="domainStore.translate('user.form.label.displayname')"
            :type="data.displayname_field.type"
            :name="data.displayname_field.code"
            :code="data.displayname_field.code"
            :required="toBool(data.displayname_field.required)"
            :public="true"
            :hidden="false"
            :serverError="data.errors[data.displayname_field.code]"
            v-on:validate="onValidatedFormField"
            :error="domainStore.translate('user.form.message.displayname-error')"
            :help="domainStore.translate('user.form.message.displayname-help')"
            parentFormId="wdw-register-form"
            defaultvalue=""
          />
          <div v-if="data.password_field != null">
            <form-control-input
              v-if="data.password_field != null"
              :label="domainStore.translate('user.form.label.password')"
              :type="data.password_field.type"
              :name="data.password_field.code"
              :code="data.password_field.code"
              :required="toBool(data.password_field.required)"
              :public="true"
              :hidden="false"
              :serverError="data.errors[data.password_field.code]"
              v-on:validate="onValidatedFormField"
              :help="domainStore.translate('user.form.message.password-register-help')"
              parentFormId="wdw-register-form"
              defaultvalue=""
            />
          </div>
          <form-control-input
            v-if="data.password_repeat_field != null"
            :label="domainStore.translate('user.form.label.password-repeat')"
            :type="data.password_repeat_field.type"
            :name="data.password_repeat_field.code"
            :code="data.password_repeat_field.code"
            :required="toBool(data.password_repeat_field.required)"
            :public="true"
            :hidden="false"
            :serverError="data.errors[data.password_repeat_field.code]"
            v-on:validate="onValidatedFormField"
            repeatFromAnotherInputId="wdw-bms-attr-text-password"
            :error="domainStore.translate('user.form.message.password-repeat-error')"
            parentFormId="wdw-register-form"
            defaultvalue=""
          />
          <div
            class="alert alert-info mt-1"
            v-dompurify-html="
              domainStore.translate('user.form.message.password-register-help-extra') + ' ' + passwordAllowedCharacters
            "
          ></div>
          <fieldset v-if="data.attribute_fields.length > 0">
            <!-- <legend>Zusatzfelder</legend> -->
            <form-control-attributes
              :fields="data.attribute_fields"
              :serverErrors="data.errors"
              :requiredIfResults="data.requiredIfResult"
              :visibleIfResults="data.visibleIfResult"
              parentFormId="wdw-register-form"
              v-on:validate="onValidatedFormField"
            />
          </fieldset>

          <form-control-checkbox
            v-if="data.accept_terms_field != null"
            :label="termsFieldLabel"
            :name="data.accept_terms_field.code"
            :code="data.accept_terms_field.code"
            :required="true"
            :public="true"
            :hidden="false"
            :serverError="data.errors[data.accept_terms_field.code]"
            :error="domainStore.translate('user.form.message.terms-error')"
            v-on:validate="onValidatedFormField"
            parentFormId="wdw-register-form"
          />

          <form-control-checkbox
            v-if="data.accept_privacy_field != null"
            :label="privacyFieldLabel"
            :name="data.accept_privacy_field.code"
            :code="data.accept_privacy_field.code"
            :required="true"
            :public="true"
            :hidden="false"
            :serverError="data.errors[data.accept_privacy_field.code]"
            :error="domainStore.translate('user.form.message.privacy-error')"
            v-on:validate="onValidatedFormField"
            parentFormId="wdw-register-form"
          />

          <fieldset v-if="data.newsletter_sub_fields.length > 0">
            <form-control-checkbox
              v-for="nl_field in data.newsletter_sub_fields"
              v-bind:key="nl_field.id"
              :label="getNewsletterLabel(nl_field)"
              :name="nl_field.code"
              :code="nl_field.code"
              :required="false"
              :hidden="false"
              :defaultvalue="toBool(nl_field.default_value)"
              :serverError="data.errors[nl_field.code]"
              v-on:validate="onValidatedFormField"
              parentFormId="wdw-register-form"
            />
          </fieldset>

          <div v-if="data.has_captcha">
            <Captcha
              :serverError="data.errors['captcha'] || ''"
              v-on:validate="onValidatedFormField"
              v-on:captcha-updated="onCaptchaUpdated"
            ></Captcha>
          </div>

          <div class="d-grid gap-2 col-6 mx-auto my-2">
            <button
              :disabled="data.loader.submitting_registration"
              class="btn btn-primary btn-lg btn-form-submit btn-register-submit"
              type="submit"
              name="submit"
            >
              {{ domainStore.translate('user.register.submit') }}
            </button>
            <vue-loading v-if="data.loader.submitting_registration" />
          </div>
        </div>
      </Form>
    </div>
    <div v-else class="col-md-12">
      <div class="alert alert-danger show mb-2" role="alert">
        {{ domainStore.translate('user.register.registration-disabled') }}
      </div>
    </div>
  </div>
</template>

<script>
import { userStore } from '@/store/user-store';
import { domainStore } from '@/store/domain-store';
import useConverter from '@/composables/general/useConverter';
import useLocalizeMessages from '@/composables/general/useLocalizeMessages';
import useFormInputDependency from '@/composables/general/useFormInputDependency';
import { onBeforeMount, onMounted, reactive, ref, computed, toRef, toRefs } from 'vue';
import { RepositoryFactory } from '@/api/repositoryFactory';
import { Form } from 'vee-validate';

import VueLoading from '@/components/general/Loading';
import FormControlAttributes from '@/components/general/FormControl/Attributes';
import FormControlInput from '@/components/general/FormControl/Input';
import FormControlCheckbox from '@/components/general/FormControl/Checkbox';
import Captcha from '@/components/general/Captcha.vue';
import InfoRequiredFields from '../general/InfoRequiredFields.vue';

const DomainRepository = RepositoryFactory.get('domain');

/* 
Still in development
upgrade to a "user" component
Should Contain:
    -[x] Registration
    -[x] Error/Success message after registration
    -[x] registration in domain repository
    -[x] getting register-config from domain repository 
    -[x] generic component to render attributes 
    -[x] captcha element if needed
*/

export default {
  name: 'Registration',

  components: {
    Form,
    Captcha,
    VueLoading,
    FormControlInput,
    FormControlCheckbox,
    FormControlAttributes,
    InfoRequiredFields,
  },

  props: {
    domainid: {
      type: Number,
      required: true,
    },
    privacyPageUri: {
      type: String,
      required: false,
    },
    termsPageUri: {
      type: String,
      required: false,
    },
  },

  setup(props) {
    const data = reactive({
      // selected fields
      email_field: null,
      displayname_field: null,
      password_field: null,
      password_repeat_field: null,
      accept_terms_field: null,
      accept_privacy_field: null,
      attribute_fields: [],
      newsletter_sub_fields: [],
      has_captcha: false,
      captcha_id: 0,
      // Flags for loader
      loader: {
        registration_config: false,
        submitting_registration: false,
      },
      registrationReady: false,
      // Server-related errors
      errors: {},
      // Evaluation of visible_if_code and visible_if_value
      visibleIfResult: {},
      // Evaluation of required_if_code and required_if_value
      requiredIfResult: {},
      // Success message
      success: '',
    });
    // formValues contain all the values and validation info submitted by the fields under <Form>
    // formValues contains more detailed information such as human-readable values, key, and validation result
    let formValues = {
      files: {},
    };

    const { evaluateFormIfCode } = useFormInputDependency();
    const { toBool } = useConverter();
    const { l } = useLocalizeMessages();

    //+--------------------+
    //|    Methods         |
    //+--------------------+
    const onSubmitRegistrationForm = (values, actions) => {
      // Show loading and clear any server error
      data.loader.submitting_registration = true;
      data.errors = [];
      data.success = '';

      // T23468
      if (values.email != values.email_repeat) {
        data.errors['email_repeat'] = domainStore.translate('user.form.message.email-repeat-error');
        data.loader.submitting_registration = false;
        return;
      }

      // If captcha exists, fix the structure so it includes the captcha param with correct keys
      if (values.captcha) {
        values['captcha_answer'] = values.captcha;
        values['captcha_id'] = data.captcha_id;
        delete values['captcha'];
      }

      // NOTE: input="file" is not automatically validated with vee-validate 4 yet (See note in FormControl/File.vue)
      //  if there's a required file input field, you will need to check it first in formValues.files
      //  values from onSubmitRegistrationForm will also not include the file object

      // Post registration data
      DomainRepository.postRegister(values)
        .then(resp => {
          data.success = domainStore.translate('user.form.message.register-success');
          data.loader.submitting_registration = false;
          // Reset the form and formValues content
          actions.resetForm();
          formValues = { files: {} };
        })
        .catch(err => {
          const errData = err.response.data.message;
          if (errData.captcha) {
            // Captcha error
            errData['captcha'] = errData.captcha;
          } else {
            for (const [key, value] of Object.entries(errData)) {
              if (value.attribute_id) {
                // Attribute error? use the attribute's own error message since we don't have localized error messages for attributes
                const attr = data.attribute_fields.filter(f => f.id === value.attribute_id);
                if (attr.length > 0) errData[key] = attr[0].error;
              } else {
                // Try to localized the error string
                errData[key] = l(errData[key]);
              }
            }
          }

          data.errors = errData;
          data.loader.submitting_registration = false;
        });
    };

    const onCaptchaUpdated = captcha => {
      data.captcha_id = captcha.captcha_id;
    };

    const onValidatedFormField = fieldValue => {
      // Triggered on "validate"-event
      if (fieldValue.type === 'file') {
        formValues['files'][fieldValue.name] = fieldValue;
      } else if (fieldValue.name == 'captcha') {
        // Special treatment for captcha
        data.captcha_id = fieldValue.data.captcha_id;
        formValues['captcha_id'] = fieldValue.data.captcha_id;
        formValues['captcha_answer'] = fieldValue.value;
      } else {
        formValues[fieldValue.name] = fieldValue;
        // Evaluate the whole attribute fields based on its visible_if_code
        data.visibleIfResult = evaluateFormIfCode(
          'visible_if_code',
          'visible_if_value',
          data.visibleIfResult,
          data.attribute_fields,
          fieldValue.code,
          fieldValue.value,
          fieldValue.type
        );
        // Evaluate the whole attribute fields based on its required_if_code
        data.requiredIfResult = evaluateFormIfCode(
          'required_if_code',
          'required_if_value',
          data.requiredIfResult,
          data.attribute_fields,
          fieldValue.code,
          fieldValue.value,
          fieldValue.type
        );
      }
    };
    const getFieldByCode = (code, attributeFields) => {
      const fields = attributeFields.filter(f => f.code == code);
      return fields.length > 0 ? fields[0] : null;
    };

    //+---------------------+
    //|   computed          |
    //+---------------------+
    // const emailHelpText = computed(() => {
    //   return (
    //     'Geben Sie hier Ihre E-Mail-Adresse ein. Ihre E-Mail-Adresse bleibt <strong>geheim</strong>. ' +
    //     'Sie wird später zur Anmeldung verwendet. Falls Sie einmal Ihr Passwort vergessen, schicken wir an diese Adresse ein neues.'
    //   );
    // });
    // const displaynameHelpText = computed(() => {
    //   return (
    //     'Dieser Name ist Ihr <strong>öffentlicher</strong> Benutzername. ' +
    //     'Er wird anderen Benutzern angezeigt, wenn Sie Beiträge auf dieser Plattform erstellen. ' +
    //     'Wenn Sie Ihren echten Namen angeben, ist dieser öffentlich sichtbar.'
    //   );
    // });
    // const passwordHelpText = computed(() => {
    //   return (
    //     'Wir empfehlen Ihnen ein Passwort zu wählen, das nicht allzu einfach zu erraten ist. ' +
    //     "'12345' oder 'Passwort' sind <strong>keine</strong> empfohlenen Passwörter. " +
    //     'Bitte geben Sie es anschließend <strong>erneut</strong> in der zweiten Eingabebox ein.'
    //   );
    // });
    const passwordAllowedCharacters = computed(() => {
      return '<strong> ! ? @ § # $ % & ( ) * + ~ - _ . , : ; < = > [ ] { } ^ ` " &#39; | / &#92; </strong>';
    });
    const termsFieldLabel = computed(() => {
      if (data.accept_terms_field == null) return '';
      let url = data.accept_terms_field.extras.url;
      if (props.termsPageUri) {
        url = props.termsPageUri;
      }
      if (url && url !== '') {
        return "<a href='" + url + "'>" + domainStore.translate('user.form.label.accept_terms') + '</a>';
      }

      return domainStore.translate('user.form.label.accept_terms');
    });
    const privacyFieldLabel = computed(() => {
      if (data.accept_privacy_field == null) return '';
      let url = data.accept_privacy_field.extras.url;
      if (props.privacyPageUri) {
        url = props.privacyPageUri;
      }
      if (url && url !== '') {
        return "<a href='" + url + "'>" + domainStore.translate('user.form.label.accept_privacy') + '</a>';
      }
      return domainStore.translate('user.form.label.accept_privacy');
    });
    const user = computed(() => {
      return userStore.getState().user;
    });
    const isLoggedIn = computed(() => {
      return userStore.isLoggedIn();
    });

    //+--------------------+
    //|    Methods         |
    //+--------------------+
    function getNewsletterLabel(nl_field) {
      if (nl_field.project_emails) {
        return domainStore.translate('user.form.label.subscribe_project');
      }
      return domainStore.translate('user.form.label.subscribe_newsletter', { name: nl_field.newsletter_name });
    }

    //+--------------------+
    //|    Lifecyle hooks  |
    //+--------------------+
    onBeforeMount(() => {
      domainStore.setDomainId(props.domainid);
    });

    onMounted(() => {
      // Retrieve registration fields from API
      data.loader.registration_config = true;
      DomainRepository.getRegistrationConfig()
        .then(function (response) {
          const resp_fields = response.data.data;
          data.email_field = getFieldByCode('email', resp_fields.default_fields);
          data.displayname_field = getFieldByCode('displayname', resp_fields.default_fields);
          data.password_field = getFieldByCode('password', resp_fields.default_fields);
          data.password_repeat_field = getFieldByCode('password_repeat', resp_fields.default_fields);
          data.accept_terms_field = getFieldByCode('accept_terms', resp_fields.default_fields);
          data.accept_privacy_field = getFieldByCode('accept_privacy_terms', resp_fields.default_fields);
          data.attribute_fields = resp_fields.attribute_fields;
          data.newsletter_sub_fields = resp_fields.newsletter_fields;
          data.has_captcha = resp_fields.DomainEnableRegistrationCaptcha;

          // Initialize the requiredIfCode from the given attribute_fields
          // This is needed because field attribute obtained from the server can be required or optional
          // for required dependency, the attribute by its own should NOT be mandatory,
          // otherwise the evaluation will not have any effect
          data.attribute_fields.forEach(f => {
            data.requiredIfResult[f.code] = toBool(f.required);
          });

          data.registrationReady = true;
          data.loader.registration_config = false;
        })
        .catch(err => {
          // Error retrieving registration fields
          data.loader.registration_config = false;
        });
    });

    return {
      domainStore,
      // Computed methods
      isLoggedIn,
      getFieldByCode,
      // emailHelpText,
      // displaynameHelpText,
      // passwordHelpText,
      termsFieldLabel,
      privacyFieldLabel,
      passwordAllowedCharacters,
      // methods:
      getNewsletterLabel,
      // Listeners
      onSubmitRegistrationForm,
      onValidatedFormField,
      onCaptchaUpdated,
      // Other methods
      toBool,
      // Data
      formValues,
      data,
    };
  },
};
</script>

<style scoped></style>
